import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../Api';

const safeStr = (value) => (value === null || value === undefined) ? '' : String(value);

export default function DisabledSKUsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [selectionModel, setSelectionModel] = useState([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const columns = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'sku', headerName: 'SKU', width: 300 },
    { field: 'fsn', headerName: 'FSN', width: 170 },
    { field: 'account_name', headerName: 'A/C', width: 180 },
    { field: 'brand', headerName: 'BRAND', width: 130 },
    { field: 'style', headerName: 'STYLE', width: 120 },
    { field: 'cmb', headerName: 'CMB', width: 100 },
    { field: 'print1', headerName: 'PRINT', width: 120 },
    { field: 'color1', headerName: 'COLOR', width: 120 },
    { field: 'real_skus', headerName: 'REAL SKU', width: 360, sortable: false },
    {
      field: 'disabled_at',
      headerName: 'EXCLUDED AT',
      width: 180,
      valueFormatter: (params) => safeStr(params.value).replace('T', ' ').replace('.000Z', ''),
    },
  ], []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/skus', {
        params: {
          disabled: 1,
          page: page + 1,
          pageSize,
          sortBy: 'disabled_at',
          sortDir: 'desc',
          sku: appliedSearch || undefined,
        },
      });
      setRows(data?.data || []);
      setRowCount(Number(data?.total || 0));
    } catch (e) {
      toast.error('Failed to load excluded SKUs: ' + (e?.response?.data?.error || e.message || 'unknown error'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedSearch]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const restoreSelected = async () => {
    const ids = (selectionModel || []).map(Number).filter(Number.isFinite);
    if (!ids.length) return toast.warn('Select excluded SKU rows to re-include');
    const ok = window.confirm(`Re-include ${ids.length} selected SKU row(s)? They will appear again in SKU tools.`);
    if (!ok) return;
    setLoading(true);
    try {
      const { data } = await api.patch('/api/skus/restore', { ids });
      toast.success(`Re-included ${data.updated || 0} SKU row(s)`);
      setSelectionModel([]);
      await fetchRows();
    } catch (e) {
      toast.error('Re-include failed: ' + (e?.response?.data?.error || e.message || 'unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    setPage(0);
    setSelectionModel([]);
    setAppliedSearch(search.trim());
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>Excluded SKUs</Typography>
            <TextField
              size="small"
              label="Search SKU"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applySearch();
              }}
              sx={{ minWidth: 240 }}
            />
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={applySearch} disabled={loading}>
              Search
            </Button>
            <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/sku-upload')}>
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<RestoreIcon />}
              onClick={restoreSelected}
              disabled={loading || selectionModel.length === 0}
            >
              Re-include Selected
            </Button>
          </Stack>

          <Box sx={{ height: 'calc(100vh - 180px)', minHeight: 520, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              checkboxSelection
              disableRowSelectionOnClick
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(model) => {
                setPage(model.page || 0);
                setPageSize(model.pageSize || 100);
              }}
              pageSizeOptions={[25, 50, 100]}
              loading={loading}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(model) => setSelectionModel(Array.isArray(model) ? model : Array.from(model || []))}
            />
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}
